import { type ComponentProps, type ReactNode, createContext, useContext } from 'react'
import { Fieldset as BaseFieldset } from '@base-ui/react/fieldset'
import { twMerge } from 'tailwind-merge'
import type { FieldOrientation } from '../index'
import type { PartProps } from '../../../lib/part-props'

interface FieldGroupContextValue {
  orientation?: FieldOrientation
}

const FieldGroupContext = createContext<FieldGroupContextValue | null>(null)

function useFieldGroupContext() {
  return useContext(FieldGroupContext)
}

/**
 * Space between fields. Rows of settings sit closer than form fields, and checkbox rows closer
 * still, because each is a single line with nothing below it to keep apart.
 */
const GROUP_GAP: Record<FieldOrientation, string> = {
  vertical: 'gap-5',
  horizontal: 'gap-5',
  justified: 'gap-4',
  leading: 'gap-3',
}

interface FieldGroupProps extends Omit<PartProps<ComponentProps<typeof BaseFieldset.Root>>, 'children'> {
  className?: string
  /** Sets every Field inside to this shape, and the spacing between them to match. */
  orientation?: FieldOrientation
  /** A heading for the set, rendered as the fieldset's legend. */
  legend?: ReactNode
  children: ReactNode
}

/**
 * A set of fields: a `<fieldset>`, so `disabled` here disables every control in it, and the
 * legend names the set to assistive tech.
 */
function FieldGroup({ className, orientation = 'vertical', legend, children, ...props }: FieldGroupProps) {
  return (
    <FieldGroupContext.Provider value={{ orientation }}>
      <BaseFieldset.Root
        data-slot="field-group"
        className={twMerge('m-0 flex w-full min-w-0 flex-col border-0 p-0', GROUP_GAP[orientation], className)}
        {...props}
      >
        {legend && (
          <BaseFieldset.Legend className="text-foreground mb-4 p-0 text-sm font-medium">{legend}</BaseFieldset.Legend>
        )}
        {children}
      </BaseFieldset.Root>
    </FieldGroupContext.Provider>
  )
}

export { FieldGroup, useFieldGroupContext }
export type { FieldGroupProps }
